import type { ParentType as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import { useMemo } from 'react';
import { z } from 'zod';

import { useGetDefaultTabs } from '@/hooks/queries';

const tabIds = [
  'acceptances',
  'actions',
  'activities',
  'appetites',
  'approvals',
  'assessments',
  'attestations',
  'audit',
  'authentication',
  'bands',
  'causes',
  'colours',
  'consequences',
  'contacts',
  'controls',
  'customRoles',
  'dataExport',
  'dataImport',
  'departments',
  'details',
  'entities',
  'entityRisks',
  'externalApi',
  'findings',
  'globalApprovals',
  'impacts',
  'indicators',
  'internalAuditRisks',
  'issues',
  'issuesBreachLog',
  'issuesConsumerDuty',
  'issuesCustomerTrust',
  'issuesGDPRBreachLog',
  'issuesPCIBreachLog',
  'issuesRiskEvents',
  'issuesSARLog',
  'linkedItems',
  'modules',
  'notificationHistory',
  'notifications',
  'questionnaires',
  'regulatoryFeed',
  'reports',
  'results',
  'sso',
  'tags',
  'taxonomy',
  'testResults',
  'updates',
  'userGroups',
  'users',
  'versions',
  'webhooks',
] as const;

export type TabId = (typeof tabIds)[number];

type TabPreference = {
  id: TabId;
  hidden?: boolean;
  demoted?: boolean;
  label?: string;
};

const tabPreferenceSchema = z.object({
  id: z.enum(tabIds),
  hidden: z.boolean().optional(),
  demoted: z.boolean().optional(),
  label: z.string().optional(),
});

const unknownListSchema = z.array(z.unknown());
const wrappedUnknownListSchema = z.object({
  default: unknownListSchema,
});

export const parseTabPreferenceList = (
  value: unknown,
  wrapped = false
): TabPreference[] | undefined => {
  const entries = wrapped
    ? wrappedUnknownListSchema.safeParse(value).data?.default
    : unknownListSchema.safeParse(value).data;
  if (!entries) {
    return undefined;
  }

  const preferences = entries.flatMap((entry) => {
    const result = tabPreferenceSchema.safeParse(entry);

    return result.success ? [result.data] : [];
  });

  return entries.length > 0 && preferences.length === 0
    ? undefined
    : preferences;
};

/**
 * Appends any tabs present in system defaults but missing from saved preferences.
 * This ensures newly added default tabs appear even when org/user preferences
 * were saved before the new tab existed.
 */
const appendMissingDefaults = (
  prefs: TabPreference[],
  defaults: TabPreference[]
): TabPreference[] => {
  const existingIds = new Set(prefs.map((t) => t.id));
  const missing = defaults.filter((t) => !existingIds.has(t.id));
  if (missing.length === 0) {
    return prefs;
  }

  return [...prefs, ...missing];
};

const useTabPreferences = (
  parentType?: Parent_Type_Enum,
  isOrgLevel?: boolean
): { tabs?: TabPreference[]; loading: boolean } => {
  const { data, loading } = useGetDefaultTabs({ queryArgs: {} });

  return useMemo(() => {
    if (loading) {
      return {
        loading: true,
      };
    }

    // Example: [{"id":"details"},{"id":"controls"},{"id":"ratings"},{"id":"appetites"},{"id":"acceptances"},{"id":"actions"},{"id":"indicators"},{"id":"approvals"},{"id":"linkedItems"}]
    const d =
      parseTabPreferenceList(
        data?.tab?.find((t) => t.ParentType === parentType)?.Tabs,
        true
      ) ?? [];
    // Example: different order, some tabs hidden
    // [{"id":"details"},{"id":"controls"},{"id":"actions"},{"id":"ratings"},{"id":"appetites"},{"id":"acceptances"},{"id":"indicators","hidden":true},{"id":"approvals","hidden":true},{"id":"linkedItems","hidden":true}]
    const o = parseTabPreferenceList(
      data?.organisation_tab_preference?.find(
        (t) => t.ObjectType === parentType
      )?.Preferences,
      true
    );
    // Can't hide tabs on user level, but can move them to a dropdown at the end
    // [{"id":"controls"},{"id":"details"},{"id":"actions"},{"id":"ratings"},{"id":"appetites","demoted":true},{"id":"acceptances","demoted":true}]
    const u = parseTabPreferenceList(
      data?.user_tab_preference?.find((t) => t.ObjectType === parentType)
        ?.Preferences
    );

    if (isOrgLevel) {
      if (!o) {
        return {
          loading: false,
          tabs: [...d],
        };
      }

      return {
        loading: false,
        tabs: appendMissingDefaults(o, d),
      };
    }

    if (!o && !u) {
      return {
        loading: false,
        tabs: d.filter((tab: TabPreference) => !tab.hidden),
      };
    }

    if (!u) {
      return {
        loading: false,
        tabs: appendMissingDefaults(o ?? [], d).filter(
          (tab: TabPreference) => !tab.hidden
        ),
      };
    }

    return {
      loading: false,
      tabs: appendMissingDefaults(u, d),
    };
  }, [loading, data, parentType, isOrgLevel]);
};

export default useTabPreferences;
